import { useState } from "react";
import { PageHeader, Section } from "@/components/layout/Section";
import { Lock } from "lucide-react";
import { PrayingHands } from "@/components/icons/PrayingHands";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function Prayer() {
  const [busy, setBusy] = useState(false);
  const [isPrivate, setIsPrivate] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const fd = new FormData(e.target);
    try {
      await api.post("/prayer", {
        name: fd.get("name"),
        email: fd.get("email") || null,
        phone: fd.get("phone") || null,
        request: fd.get("request"),
        is_private: isPrivate,
      });
      toast.success("Your request is received. We're praying with you.");
      e.target.reset();
      setIsPrivate(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="prayer-page">
      <PageHeader
        kicker="Prayer Request"
        title="Let's stand together in prayer."
        subtitle="Whatever you're facing, you don't face it alone. Share your request and our intercessors will pray with you."
      />

      <Section>
        <div className="max-w-2xl mx-auto card-soft p-10">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-full bg-gold/15 text-gold-500 flex items-center justify-center">
              <PrayingHands className="w-6 h-6" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-gold-500">Confidential</div>
              <div className="font-serif text-2xl text-royal">Submit your request</div>
            </div>
          </div>

          <form onSubmit={submit} className="grid gap-5" data-testid="prayer-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label>Your name</Label>
                <Input name="name" required className="mt-2" data-testid="prayer-name" />
              </div>
              <div>
                <Label>Phone (optional)</Label>
                <Input name="phone" className="mt-2" data-testid="prayer-phone" />
              </div>
            </div>
            <div>
              <Label>Email (optional)</Label>
              <Input type="email" name="email" className="mt-2" data-testid="prayer-email" />
            </div>
            <div>
              <Label>Your request</Label>
              <Textarea name="request" required rows={6} className="mt-2" data-testid="prayer-request" />
            </div>
            <label className="flex items-center gap-3 text-sm text-slate-600 cursor-pointer">
              <Checkbox checked={isPrivate} onCheckedChange={(v) => setIsPrivate(!!v)} data-testid="prayer-private" />
              <span className="flex items-center gap-2"><Lock className="w-3.5 h-3.5 text-gold-500" /> Keep this request private (visible only to pastors)</span>
            </label>

            <button disabled={busy} className="btn-primary w-fit" data-testid="prayer-submit">
              Send Request
            </button>
          </form>
        </div>
      </Section>
    </div>
  );
}
